import { guides } from './guides'
import { articles } from './articles'
import { programTemplates, programsIndexSlug } from './programTemplates'
import { degreeCategories } from './degrees'
import { universities } from './universities'
import { findDegreesTemplates } from './findDegreesTemplates'
import { degreeProgramCombos } from '../utils/degreeSlugs'

export interface SitePage {
  label: string
  to: string
  /** Short supporting line shown under the label. */
  detail?: string
  /** Extra words folded into search but not displayed. */
  keywords?: string
}

export interface SiteTemplate {
  /** Stable id used as a filter value. */
  id: string
  label: string
  /** The route pattern this template serves. */
  pattern: string
  description: string
  /** Index/hub page for this template, when it has one. */
  indexTo?: string
  pages: SitePage[]
}

function slugToLabel(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/**
 * Every route template on the site with the complete set of pages it serves.
 *
 * Built from the same data the pages render from, so a new guide, article, or
 * program appears here automatically. Shared by the footer dropdowns and the
 * all-pages search at /pages.
 */
export const siteTemplates: SiteTemplate[] = [
  {
    id: 'guides',
    label: 'Guides',
    pattern: '/guide/:slug',
    description:
      'Themed guides pairing editorial context with accredited partner listings.',
    pages: guides.map((guide) => ({
      label: guide.title,
      to: `/guide/${guide.slug}`,
      detail: guide.metaDescription,
    })),
  },
  {
    id: 'articles',
    label: 'Articles',
    pattern: '/article/:slug',
    description:
      'Long-form guides on accreditation, admissions, aid, and careers.',
    indexTo: '/articles',
    pages: articles.map((article) => ({
      label: article.title,
      to: `/article/${article.slug}`,
      detail: article.category,
      keywords: `${article.targetKeyword} ${article.intro}`,
    })),
  },
  {
    id: 'program-information',
    label: 'Program Information',
    pattern: '/program-information/:template',
    description:
      'Detailed pages for each new online program, grouped into six domains.',
    indexTo: `/program-information/${programsIndexSlug}`,
    pages: programTemplates.map((program) => ({
      label: program.name,
      to: `/program-information/${program.slug}`,
      detail: `${program.credential} · ${program.weeks} weeks · ${program.credits} credits`,
      keywords: program.metaDescription,
    })),
  },
  {
    id: 'find-degrees',
    label: 'Degree Program Pages',
    pattern: '/find-degrees/:degree-:mProgram',
    description:
      'A landing page for every degree level and specialization combination.',
    indexTo: '/find-degrees',
    pages: degreeProgramCombos.map((combo) => ({
      label: combo.title,
      to: `/find-degrees/${combo.slug}`,
      detail: combo.areaLabel,
    })),
  },
  {
    id: 'degree-categories',
    label: 'Degree Categories',
    pattern: '/degrees/:category',
    description: 'Category listing pages with live accredited school results.',
    pages: degreeCategories.map((category) => ({
      label: category.name,
      to: `/degrees/${category.id}`,
      detail: category.description,
    })),
  },
  {
    id: 'colleges',
    label: 'Partner Colleges',
    pattern: '/colleges/:universitySlug',
    description: 'Dedicated landing pages for each partner school.',
    indexTo: '/colleges',
    pages: universities.map((university) => ({
      label: university.name,
      to: `/colleges/${university.slug}`,
      detail: university.location,
      keywords: `${university.accreditation} ${university.programs
        .map((p) => p.name)
        .join(' ')}`,
    })),
  },
  {
    id: 'themes',
    label: 'Theme Landing Pages',
    pattern: '/find-degrees/:template',
    description: 'Campaign landing pages built around a single theme.',
    pages: findDegreesTemplates.map((template) => ({
      label: slugToLabel(template.slug),
      to: `/find-degrees/${template.slug}`,
      detail: template.metaDescription,
    })),
  },
]

/** Flat list of every page, tagged with the template it belongs to. */
export interface FlatSitePage extends SitePage {
  templateId: string
  templateLabel: string
}

export const allSitePages: FlatSitePage[] = siteTemplates.flatMap((template) =>
  template.pages.map((page) => ({
    ...page,
    templateId: template.id,
    templateLabel: template.label,
  })),
)
